import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const { tenantPath } = useTenant();
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    
    const verifyEmail = async () => {
      try {
        await api.post('/auth/verify-email', { token });
        setStatus('success');
        toast.success('Email verified successfully!');
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
        toast.error('Email verification failed');
      }
    };
    
    verifyEmail();
  }, [token]);
  
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <img src="/logo.png" alt="Golfshop.fit" className="h-20 w-20 mx-auto mb-12 rounded-full object-cover" />
        
        <div className="bg-gray-50 p-12 rounded-lg border border-[#F3F4F6]">
          {status === 'verifying' && (
            <div className="space-y-6">
              <Loader className="h-16 w-16 text-[#334155] mx-auto animate-spin" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Email</h2>
                <p className="text-gray-600">Please wait...</p>
              </div>
            </div>
          )}
          
          {status === 'success' && (
            <div className="space-y-6" data-testid="success-message">
              <CheckCircle className="h-16 w-16 text-[#334155] mx-auto" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
                <p className="text-gray-600 mb-6">
                  Your email has been verified successfully. You can now access all features.
                </p>
                <Link
                  to={tenantPath("/dashboard")}
                  className="inline-block h-12 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all"
                >
                  Go to Dashboard
                </Link>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-6">
              <XCircle className="h-16 w-16 text-[#EF4444] mx-auto" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
                <p className="text-gray-600 mb-6">
                  The verification link is invalid or has expired.
                </p>
                <Link
                  to={tenantPath("/login")}
                  className="inline-block h-12 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
